/* deploy: 2026-05-29 v2 */
/*
 * cedarfur-cart.js
 *
 * Cart reconciler + drawer renderer.
 *
 * Responsibilities:
 *  1. Orphan-gift removal — when pendant qty drops below the unlock
 *     threshold, remove the corresponding gift line(s) via /cart/change.js.
 *  2. Gift hydration — when pendant qty rises into a new tier, ADD the
 *     missing gift(s) via /cart/add.js.
 *  3. Drawer wiring — listens for change events on the drawer's pendant
 *     qty dropdown (`select[data-cf-drawer-pendant-qty]`), reconciles,
 *     and dispatches `cf:cart:updated` on document for the drawer agent
 *     to re-render against.
 *  4. Drawer rendering — `window.cfCart.renderDrawer(cart)` clones the
 *     `#cf-drawer-item-template` per line item, updates subtotal +
 *     shipping progress + item count.
 *
 * Public API exposed (consumed by sibling drawer JS):
 *   window.cfCart = {
 *     renderDrawer: function (cart) {...},
 *     syncGifts:    function () { -> Promise<cart> },
 *     reconcile:    function () { -> Promise<cart> }   // syncGifts alias
 *   };
 *
 * Idempotency: syncGifts() is safe to call repeatedly. It computes the
 * target gift set from the current pendant qty and the existing cart
 * state, then issues ONLY the necessary add + remove calls. Running it
 * twice on a settled cart is a no-op (no network mutation beyond the
 * /cart.js read).
 *
 * Diagnosis note on the previous orphan bug:
 *   The old version guarded with sessionStorage flag `cf_sync_just_ran`
 *   and a hard `window.location.reload()` after each removal. The audit
 *   surfaced cases where a 3 -> 2 qty change left BOTH gifts in cart.
 *   Root cause: the flag-and-reload loop was inherently racy. After the
 *   PDP add-to-cart redirect to /cart, syncGifts() runs once (no orphans,
 *   no flag set, no reload). On native Shopify qty-change submit, /cart
 *   reloads and the flag is NOT set, so syncGifts() reads /cart.js — but
 *   on some Shopify CDN paths /cart.js can briefly return STALE data
 *   reflecting the pre-change qty, so no orphans are detected, no
 *   removal fires. The page renders with both gifts still present.
 *
 *   Fix: drop the flag + reload pattern entirely. Instead:
 *     - syncGifts() returns the live cart and only mutates when the
 *       state truly disagrees with the threshold.
 *     - On the /cart page, after mutating, we re-fetch /cart.js and
 *       reload only if mutations actually occurred AND the page has no
 *       drawer to re-render into. (Drawer flow doesn't reload at all.)
 *     - Idempotency guarantees that a stale-data false-negative on
 *       cycle N is corrected on cycle N+1 (next drawer change, next
 *       page interaction, or next visit).
 */
(function () {
  'use strict';

  // Bundle product handles — refactored 2026-05-29 to use Shopify Bundles
  // app products instead of qty-based gift synthesis. Each handle is a
  // distinct cart line; no auto-add gift sync, no orphan removal, no
  // qty-to-gift mapping. Drawer just renders whatever's in the cart.
  var BUNDLE_HANDLES = {
    '1pack': 'single-pendant',
    '2pack': '2-pack-bundle',
    '3pack': '3-pack-bundle'
  };
  var FREE_SHIP_THRESHOLD_CENTS = 4500; // $45 — matches PDP / drawer contract

  var FETCH_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };
  var READ_HEADERS = {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  // ------------------------------------------------------------------
  // Cart state cache (module-scope)
  //
  // _lastCart: latest cart state seen by renderDrawer(). Used by the
  // drawer-open hook to paint stale state immediately while /cart.js
  // refreshes in the background.
  //
  // _lastCartAt: epoch-ms timestamp of last _lastCart write. Drawer-open
  // hook uses a freshness check (5s window) to decide whether to skip
  // the skeleton.
  // ------------------------------------------------------------------
  var _lastCart = null;
  var _lastCartAt = 0;
  // Monotonic render token. Each renderDrawer() call claims the next value;
  // a deferred (decode-gated) commit aborts if a newer render has started,
  // so an optimistic paint can never clobber a later authoritative one.
  var _renderToken = 0;

  // ------------------------------------------------------------------
  // 3-pack variant prefetch cache (module-scope)
  //
  // Populated by renderUpsell() the moment the strip becomes visible so
  // the upgrade click path can skip find3packVariant() entirely (saving
  // one /products/3-pack-bundle.js fetch + parse round trip).
  //
  // _prefetch3pack: the resolved variant object (or null if not yet
  //   resolved or if resolution failed).
  // _prefetch3packColor: the option1/color string the prefetch was run
  //   for. We re-resolve if the current 2-pack line's color differs
  //   (e.g. the customer swapped colors between renders).
  // ------------------------------------------------------------------
  var _prefetch3pack = null;
  var _prefetch3packColor = null;

  // ------------------------------------------------------------------
  // Cart I/O
  // ------------------------------------------------------------------
  function getCartState() {
    return fetch('/cart.js', {
      headers: READ_HEADERS,
      credentials: 'same-origin',
      // Defensive: dodge any aggressive CDN cache on the /cart.js endpoint.
      cache: 'no-store'
    }).then(function (r) { return r.json(); });
  }

  function removeLineByKey(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: FETCH_HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function (r) {
      if (!r.ok) throw new Error('Failed to remove line ' + key);
      return r.json();
    });
  }

  function changeLineQty(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: FETCH_HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('Failed to change line ' + key);
      return r.json();
    });
  }

  // syncGifts() used to do qty-based orphan-add/remove of gift lines.
  // After the move to Shopify Bundles app products (2026-05-29) each
  // bundle is one cart line with its own gifts baked in — no orphan
  // logic needed. Kept as a no-op shim so external callers (PDP /cart
  // page bootstrap, the public window.cfCart.syncGifts API) still work.
  function syncGifts(seedCart) {
    if (seedCart && seedCart.items) return Promise.resolve(seedCart);
    return getCartState().catch(function () { return null; });
  }

  // ------------------------------------------------------------------
  // Drawer rendering
  // ------------------------------------------------------------------
  // Update every cart-count badge in the page chrome (header nav cart icon).
  // The nav badge used to be a hardcoded "0" that nothing ever updated, so it
  // stayed at 0 after adds and across drawer open/close. Now it tracks the live
  // item_count: revealed + set when >0, hidden when the cart is empty.
  function updateCartBadges(cart) {
    var n = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    var els = document.querySelectorAll('[data-cf-cart-count]');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(n);
      if (n > 0) { els[i].hidden = false; els[i].removeAttribute('hidden'); }
      else { els[i].hidden = true; els[i].setAttribute('hidden', ''); }
    }
  }

  function formatMoney(cents) {
    var dollars = (cents || 0) / 100;
    try {
      return '$' + dollars.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (e) {
      return '$' + dollars.toFixed(2);
    }
  }

  // Compute a footer note that aligns with the current cart state
  // instead of always repeating "Free US shipping on Buy 2+".
  function deriveFooterNote(cart) {
    if (!cart) return '30-day guarantee';
    if ((cart.total_price || 0) >= FREE_SHIP_THRESHOLD_CENTS) {
      return 'Free US shipping · 30-day guarantee';
    }
    var remaining = FREE_SHIP_THRESHOLD_CENTS - (cart.total_price || 0);
    return 'Add ' + formatMoney(remaining) + ' to unlock free US shipping';
  }

  // Render the empty state — clones #cf-drawer-empty-template into the
  // items container and toggles cf-cart-drawer--empty on the drawer root
  // so the CSS can hide the footer + shipping bar + checkout button.
  function renderEmpty(container) {
    var empty = document.getElementById('cf-drawer-empty-template');
    if (!container) return;
    var fragment = document.createDocumentFragment();
    if (empty && empty.content) {
      fragment.appendChild(empty.content.cloneNode(true));
    } else {
      var fallback = document.createElement('div');
      fallback.className = 'cf-drawer-empty';
      var p = document.createElement('p');
      p.className = 'cf-drawer-empty__title';
      p.textContent = 'Your cart is empty';
      fallback.appendChild(p);
      fragment.appendChild(fallback);
    }
    container.replaceChildren(fragment);
    var root = document.getElementById('cf-cart-drawer');
    if (root) root.classList.add('cf-cart-drawer--empty');
    var subtotalEl = document.querySelector('[data-cf-drawer-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(0);
    var countEl = document.querySelector('[data-cf-drawer-item-count]');
    if (countEl) countEl.textContent = '0';
    var checkout = document.querySelector('[data-cf-drawer-checkout]');
    if (checkout) checkout.disabled = true;
    updateCartBadges({ item_count: 0 });
  }

  // Render the skeleton loader — used by the drawer-open hook BEFORE
  // /cart.js resolves to avoid the 1.4s flash-of-empty-cart.
  function renderSkeleton(container) {
    var tpl = document.getElementById('cf-drawer-skeleton-template');
    if (!container || !tpl || !tpl.content) return;
    var fragment = document.createDocumentFragment();
    fragment.appendChild(tpl.content.cloneNode(true));
    container.replaceChildren(fragment);
    var root = document.getElementById('cf-cart-drawer');
    if (root) {
      root.classList.add('cf-cart-drawer--loading');
      root.classList.remove('cf-cart-drawer--empty');
    }
  }

  function renderDrawer(cart, allowEmpty) {
    if (!cart) return;
    var container = document.getElementById('cf-cart-drawer-items');
    var template = document.getElementById('cf-drawer-item-template');
    if (!container || !template || !template.content) return;
    var root = document.getElementById('cf-cart-drawer');

    // Defensive short-circuit: if a stale/partial-failure response hands
    // us an empty cart within 5s of the last known non-empty render,
    // preserve the prior render. Legit "user cleared cart in another tab"
    // events land on the next render once the freshness window expires.
    var nowMs = Date.now();
    var incomingEmpty = !cart.items || !cart.items.length;
    if (incomingEmpty && !allowEmpty && _lastCart && _lastCart.items && _lastCart.items.length > 0 && (nowMs - _lastCartAt) < 5000) {
      if (window && window.console) {
        console.warn('[cedarfur-cart] suppressed empty-cart render — last known cart had ' + _lastCart.items.length + ' items');
      }
      return;
    }

    // Reset loading state — we have real data now.
    if (root) root.classList.remove('cf-cart-drawer--loading');

    var items = cart.items || [];

    // Empty cart -> empty state.
    if (!items.length) {
      renderEmpty(container);
      _lastCart = cart; // still stash so freshness checks see "we know it's empty"
      _lastCartAt = nowMs;
      return;
    }

    // Real cart -> clear the empty class + restore checkout
    if (root) root.classList.remove('cf-cart-drawer--empty');
    var checkout = document.querySelector('[data-cf-drawer-checkout]');
    if (checkout) checkout.disabled = false;

    // Build all line-item clones into a DocumentFragment first, then
    // atomically swap with replaceChildren(). This collapses the entire
    // items update into a single paint — no blank-container frame is
    // ever committed between the innerHTML clear and the appendChild loop.
    var fragment = document.createDocumentFragment();
    var pendingThumbs = [];
    var myToken = ++_renderToken;

    // Snapshot already-decoded thumbnails by VARIANT id. When a re-render
    // (optimistic -> authoritative) shows the same variant, adopt the existing
    // decoded <img> node and keep its src, so the thumbnail never rebuilds into
    // a blank (naturalWidth 0) frame — that was the "bundle image glitches"
    // flash. Keyed by variant id (stable across both renders), NOT line key
    // (the optimistic key differs) and NOT src (the line-item image URL and the
    // variant featured_image URL can differ by ?v= / size params).
    var existingByVid = {};
    var liveLines = container.querySelectorAll('.cf-drawer-item[data-cf-variant-id]');
    for (var lt = 0; lt < liveLines.length; lt++) {
      var vk = liveLines[lt].getAttribute('data-cf-variant-id');
      var im = liveLines[lt].querySelector('.cf-drawer-item__thumb');
      if (vk && im && im.complete && im.naturalWidth > 0) existingByVid[vk] = im;
    }

    items.forEach(function (item) {
      var clone = template.content.cloneNode(true);
      var rootEl = clone.querySelector('.cf-drawer-item');
      if (rootEl) {
        rootEl.setAttribute('data-line-key', item.key);
        // Stamp the variant id so the next render can adopt this line's
        // already-decoded thumbnail (see existingByVid above).
        rootEl.setAttribute('data-cf-variant-id', String(item.variant_id || item.id || ''));
        // Strip the template marker so live elements are never confused
        // for the inert template node by any future querySelectorAll scan.
        rootEl.removeAttribute('data-cf-template');
      }
      var templateMarkedSelect = clone.querySelector('select[data-cf-template]');
      if (templateMarkedSelect) templateMarkedSelect.removeAttribute('data-cf-template');

      var thumb = clone.querySelector('.cf-drawer-item__thumb');
      if (thumb) {
        if (item.image) {
          var vkey = String(item.variant_id || item.id || '');
          var adopt = existingByVid[vkey];
          if (adopt && thumb.parentNode) {
            // Same variant already shown with a decoded image — reuse that exact
            // node (keep its src) so there is zero blank-frame rebuild.
            adopt.alt = item.product_title || item.title || '';
            thumb.parentNode.replaceChild(adopt, thumb);
            delete existingByVid[vkey]; // don't adopt the same node twice
          } else {
            thumb.src = item.image;
            thumb.alt = item.product_title || item.title || '';
            thumb.onerror = function () { this.removeAttribute('src'); };
            // Collect so we can decode BEFORE the atomic swap. Without this,
            // a fresh <img> blanks (naturalWidth 0) for a frame or two before
            // it decodes — the "image glitches" flash.
            pendingThumbs.push(thumb);
          }
        } else {
          thumb.removeAttribute('src');
          thumb.alt = '';
        }
      }

      var title = clone.querySelector('.cf-drawer-item__title');
      if (title) title.textContent = item.product_title || item.title || '';

      // Per-line variant subtitle (e.g., "Color: Green")
      var variantEl = clone.querySelector('.cf-drawer-item__variant');
      if (variantEl) {
        var vt = item.variant_title || (item.options_with_values && item.options_with_values.map(function (o) { return o.value; }).join(' / ')) || '';
        if (vt && vt.toLowerCase() !== 'default title') {
          variantEl.textContent = vt;
          variantEl.hidden = false;
        } else {
          variantEl.hidden = true;
        }
      }

      // Bundle gift inclusion line — mirrors the PDP bundle__bonus copy
      // so the customer knows what's coming with their bundle.
      var giftsEl = clone.querySelector('.cf-drawer-item__gifts');
      if (giftsEl) {
        var handle = item.handle || item.product_handle || '';
        var giftsText = '';
        if (handle === '2-pack-bundle' || handle === '3-pack-bundle') {
          giftsText = '+ Free Found-a-Tick Field Guide + Pendant Tracker';
        }
        if (giftsText) {
          giftsEl.textContent = giftsText;
          giftsEl.hidden = false;
        } else {
          giftsEl.hidden = true;
        }
      }

      // Per-line qty stepper — every cart line gets +/- controls now.
      var qtyDisplay = clone.querySelector('.cf-drawer-item__qty-display');
      if (qtyDisplay) qtyDisplay.textContent = String(item.quantity || 1);
      var stepMinus = clone.querySelector('[data-cf-drawer-step="-1"]');
      var stepPlus = clone.querySelector('[data-cf-drawer-step="+1"]');
      if (stepMinus) stepMinus.disabled = false;
      if (stepPlus) stepPlus.disabled = false;

      // Per-line price
      var priceEl = clone.querySelector('.cf-drawer-item__price');
      if (priceEl) {
        var lp = (item.final_line_price != null) ? item.final_line_price : item.line_price;
        if (lp === 0) {
          priceEl.textContent = 'Free';
          priceEl.classList.add('cf-drawer-item__price--free');
        } else {
          priceEl.textContent = formatMoney(lp);
          priceEl.classList.remove('cf-drawer-item__price--free');
        }
      }

      fragment.appendChild(clone);
    });

    function commit() {
    // Abort if a newer render has superseded this one (prevents a deferred
    // optimistic commit from overwriting a fresher authoritative render).
    if (myToken !== _renderToken) return;

    // Single atomic DOM swap — one paint, no empty-container frame.
    container.replaceChildren(fragment);

    // Subtotal
    var subtotalEl = document.querySelector('[data-cf-drawer-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Shipping progress
    var fill = document.querySelector('[data-cf-drawer-shipping-progress-fill]');
    var caption = document.querySelector('[data-cf-drawer-shipping-caption]');
    if (fill) {
      var pct = Math.min(100, ((cart.total_price || 0) / FREE_SHIP_THRESHOLD_CENTS) * 100);
      fill.style.width = pct + '%';
    }
    if (caption) {
      if ((cart.total_price || 0) >= FREE_SHIP_THRESHOLD_CENTS) {
        caption.textContent = 'Free US shipping unlocked';
      } else {
        var remaining = FREE_SHIP_THRESHOLD_CENTS - (cart.total_price || 0);
        caption.textContent = 'Add ' + formatMoney(remaining) + ' more for free US shipping';
      }
    }

    // Footer note — dynamic, no longer contradicts the "unlocked" caption.
    var footerNote = document.querySelector('[data-cf-drawer-footer-note]');
    if (footerNote) footerNote.textContent = deriveFooterNote(cart);

    // Post-decision upsell: 2-pack in cart + no 3-pack -> offer the upgrade.
    renderUpsell(cart);

    // Item count badge (drawer header)
    var countEl = document.querySelector('[data-cf-drawer-item-count]');
    if (countEl) countEl.textContent = String(cart.item_count || 0);

    // Header nav cart badge — keep it in sync with every render.
    updateCartBadges(cart);

    // Stash for the qty-change handler's optimistic render path.
    _lastCart = cart;
    _lastCartAt = Date.now();
    }

    // Decode-gate the commit: wait for the new thumbnails to decode so the
    // swap paints a ready image instead of a blank frame. Images already in
    // the browser cache (e.g. the same bundle image across optimistic ->
    // authoritative renders) decode in well under a frame, so this is
    // invisible. A 120ms safety race guarantees a broken/slow image never
    // stalls the drawer, and the render-token check inside commit() keeps
    // racing renders ordered.
    if (typeof Promise === 'undefined' || !pendingThumbs.length) {
      commit();
    } else {
      var committed = false;
      var finish = function () { if (committed) return; committed = true; commit(); };
      // Gate the swap on an actual DECODE (via a detached probe with the same
      // src), not just 'load'. decode() resolves only once the bitmap is ready
      // to paint, so the committed <img> (same cached URL) never shows a blank
      // (naturalWidth 0) frame. The 600ms race guarantees a slow/broken image
      // can never stall the drawer open.
      var gates = pendingThumbs.map(function (img) {
        var src = img && img.getAttribute('src');
        if (!src) return Promise.resolve();
        return new Promise(function (resolve) {
          var probe = new Image();
          probe.src = src;
          if (probe.decode) {
            probe.decode().then(resolve, function () { resolve(); });
          } else if (probe.complete && probe.naturalWidth > 0) {
            resolve();
          } else {
            probe.addEventListener('load', resolve, { once: true });
            probe.addEventListener('error', resolve, { once: true });
          }
        });
      });
      Promise.all(gates).then(finish);
      setTimeout(finish, 600);
    }
  }

  // ------------------------------------------------------------------
  // Upgrade-to-3-pack upsell (post-decision AOV lever)
  //
  // Shown when the cart holds a 2-pack line and no 3-pack line. Click
  // swaps the 2-pack for the 3-pack in the SAME color: add 3-pack first,
  // then remove the 2-pack (add-then-remove so a failure never leaves an
  // empty cart). Variant resolution: window.cfBundles['3pack'] when the
  // PDP defined it, else the public /products/3-pack-bundle.js endpoint.
  // ------------------------------------------------------------------
  var _upsellWired = false;
  var _upsellShownTagged = false;

  function clarityTag(ev) {
    try { if (window.clarity) window.clarity('event', ev); } catch (_) {}
  }

  function find3packVariant(color) {
    var norm = String(color || '').trim().toLowerCase();
    function pick(product) {
      if (!product || !product.variants) return null;
      var fallback = product.variants[0] || null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        var opt = String(v.option1 || v.title || '').trim().toLowerCase();
        if (norm && opt === norm) return v;
      }
      return fallback;
    }
    if (window.cfBundles && window.cfBundles['3pack']) {
      return Promise.resolve(pick(window.cfBundles['3pack']));
    }
    return fetch('/products/' + BUNDLE_HANDLES['3pack'] + '.js', {
      headers: READ_HEADERS, credentials: 'same-origin'
    }).then(function (r) { return r.json(); }).then(pick);
  }

  function renderUpsell(cart) {
    var box = document.getElementById('cf-drawer-upsell');
    if (!box) return;
    var items = (cart && cart.items) || [];
    var twoPackLine = null;
    var hasThreePack = false;
    for (var i = 0; i < items.length; i++) {
      var h = items[i].handle || items[i].product_handle || '';
      if (h === BUNDLE_HANDLES['2pack'] && !twoPackLine) twoPackLine = items[i];
      if (h === BUNDLE_HANDLES['3pack']) hasThreePack = true;
    }
    var show = !!twoPackLine && !hasThreePack;
    box.hidden = !show;
    if (!show) return;

    if (!_upsellShownTagged) { clarityTag('cf_upsell_shown'); _upsellShownTagged = true; }

    // --- PREFETCH at strip-show ---
    // Resolve the 3-pack variant for the current 2-pack line's color the
    // moment the strip becomes visible — before the customer has a chance
    // to click. Cache in module vars so the click handler can skip the
    // network round trip entirely.
    //
    // Re-resolve if the cached color no longer matches (e.g. the customer
    // had a different color variant in the cart on a previous drawer open).
    var currentColor = twoPackLine.variant_title ||
      (twoPackLine.options_with_values && twoPackLine.options_with_values[0] && twoPackLine.options_with_values[0].value) || '';
    var colorNorm = String(currentColor || '').trim().toLowerCase();
    var cacheColorNorm = String(_prefetch3packColor || '').trim().toLowerCase();
    if (!_prefetch3pack || cacheColorNorm !== colorNorm) {
      _prefetch3pack = null; // invalidate stale entry before the async resolves
      _prefetch3packColor = currentColor;
      find3packVariant(currentColor).then(function (variant) {
        if (!variant) return;
        // Only commit the cache if the color context hasn't changed while
        // the fetch was in-flight (the user could open/close the drawer
        // quickly and land on a different color).
        if (String(_prefetch3packColor || '').trim().toLowerCase() === colorNorm) {
          _prefetch3pack = variant;
        }
        // Warm the 3-pack thumbnail in the browser image cache so the
        // drawer never shows a blank frame when the customer upgrades.
        // featured_image can be a string URL or an object with a .src
        // property — handle both defensively and fall back silently.
        try {
          var imgSrc = null;
          if (variant.featured_image) {
            if (typeof variant.featured_image === 'string') {
              imgSrc = variant.featured_image;
            } else if (variant.featured_image && typeof variant.featured_image.src === 'string') {
              imgSrc = variant.featured_image.src;
            }
          }
          if (imgSrc) {
            var warmImg = new Image();
            warmImg.src = imgSrc;
          }
        } catch (_) {}
      }).catch(function () {
        // Prefetch failure is silent — the click path has a fallback.
      });
    }

    var btn = document.getElementById('cf-drawer-upsell-btn');
    if (!btn || _upsellWired) return;
    _upsellWired = true;
    var _btnOriginalLabel = btn.textContent;
    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      // Give instant feedback — set loading label before any async work.
      btn.disabled = true;
      btn.textContent = 'Upgrading…';
      clarityTag('cf_upsell_clicked');

      // --- CART STATE: use _lastCart when fresh (<5s), else fetch ---
      // Skips one /cart.js round trip when the drawer was just rendered
      // (the common case — customer sees the upsell strip and clicks
      // within seconds of the drawer opening).
      var cartPromise = (_lastCart && (Date.now() - _lastCartAt) < 5000)
        ? Promise.resolve(_lastCart)
        : getCartState();

      cartPromise.then(function (fresh) {
        var line = null;
        var freshItems = (fresh && fresh.items) || [];
        for (var j = 0; j < freshItems.length; j++) {
          var fh = freshItems[j].handle || freshItems[j].product_handle || '';
          if (fh === BUNDLE_HANDLES['2pack']) { line = freshItems[j]; break; }
        }
        if (!line) { btn.disabled = false; btn.textContent = _btnOriginalLabel; return; }

        var clickColor = line.variant_title ||
          (line.options_with_values && line.options_with_values[0] && line.options_with_values[0].value) || '';
        var clickColorNorm = String(clickColor || '').trim().toLowerCase();
        var cachedColorNorm = String(_prefetch3packColor || '').trim().toLowerCase();

        // --- VARIANT: use prefetch cache when color matches ---
        // Only call find3packVariant() if the cache is empty or the color
        // has drifted since the strip was shown (edge case).
        var variantPromise = (_prefetch3pack && cachedColorNorm === clickColorNorm)
          ? Promise.resolve(_prefetch3pack)
          : find3packVariant(clickColor);

        return variantPromise.then(function (variant) {
          if (!variant) throw new Error('No 3-pack variant resolved');
          // Single atomic /cart/update.js: removes the 2-pack line (key -> 0)
          // and adds the 3-pack variant in ONE round trip — replaces the old
          // serial add-then-remove pair (~2 RTTs) that dominated upgrade
          // latency. update.js validates the whole batch, so a failure
          // leaves the cart untouched (never an empty cart mid-swap).
          var updates = {};
          updates[line.key] = 0;
          updates[String(variant.id)] = line.quantity || 1;
          return fetch('/cart/update.js', {
            method: 'POST', headers: FETCH_HEADERS, credentials: 'same-origin',
            body: JSON.stringify({ updates: updates })
          }).then(function (r) {
            if (!r.ok) throw new Error('3-pack swap failed');
            return r.json();
          }).then(function (updated) {
            clarityTag('cf_upsell_upgraded');
            btn.disabled = false;
            btn.textContent = _btnOriginalLabel;
            if (updated) {
              // Pass the /cart/update.js response straight to renderDrawer
              // and dispatch the event — no extra getCartState() round trip.
              renderDrawer(updated);
              try { document.dispatchEvent(new CustomEvent('cf:cart:updated', { detail: updated })); } catch (_) {}
            }
          });
        });
      }).catch(function () {
        // Error path: restore button, show error banner.
        btn.disabled = false;
        btn.textContent = _btnOriginalLabel;
        showCartError("Couldn't upgrade just now — try again.");
      });
    });
  }

  // Public helpers for the PDP / error-banner.
  // Call cfCart.showError() when /cart/add.js fails (e.g. Cloudflare 403);
  // the PDP can wire this from its ATC error path.
  function showCartError(message) {
    var err = document.getElementById('cf-cart-drawer-error');
    if (!err) return;
    if (typeof message === 'string' && message.length) {
      err.textContent = message;
    }
    err.hidden = false;
  }
  function hideCartError() {
    var err = document.getElementById('cf-cart-drawer-error');
    if (err) err.hidden = true;
  }

  // ------------------------------------------------------------------
  // Optimistic single-line cart prediction
  //
  // For per-line +/- stepper clicks. Each cart line stands alone — no
  // gift orphan/add reconciliation, no cross-line dependencies.
  // ------------------------------------------------------------------
  function predictCartAfterQtyChange(baseCart, lineKey, newQty) {
    if (!baseCart || !baseCart.items) return null;
    var next;
    try { next = JSON.parse(JSON.stringify(baseCart)); }
    catch (_) { return null; }

    var keepItems = [];
    var hit = false;
    next.items.forEach(function (item) {
      if (item.key !== lineKey) { keepItems.push(item); return; }
      hit = true;
      if (newQty <= 0) return; // line removed
      var unit = item.original_price || item.price || 0;
      if (unit <= 0 && item.quantity > 0) {
        unit = Math.round((item.line_price || 0) / item.quantity);
      }
      item.quantity = newQty;
      item.line_price = unit * newQty;
      item.original_line_price = unit * newQty;
      item.final_line_price = unit * newQty;
      keepItems.push(item);
    });
    if (!hit) return null;
    next.items = keepItems;

    var totalPrice = 0, itemCount = 0;
    next.items.forEach(function (i) {
      totalPrice += (i.final_line_price != null) ? i.final_line_price : (i.line_price || 0);
      itemCount += (i.quantity || 0);
    });
    next.total_price = totalPrice;
    next.items_subtotal_price = totalPrice;
    next.item_count = itemCount;
    return next;
  }

  // ------------------------------------------------------------------
  // Drawer event wiring
  //
  // The drawer template renders dynamically — delegate from document so
  // the listener survives re-renders.
  // ------------------------------------------------------------------
  function findLineKeyFromTarget(el) {
    while (el && el !== document) {
      if (el.getAttribute && el.getAttribute('data-line-key')) {
        return el.getAttribute('data-line-key');
      }
      el = el.parentNode;
    }
    return null;
  }

  // Remove-button click handler — delegated from document. Triggered by
  // [data-cf-drawer-remove] buttons on each line item. Reads the line key
  // from the nearest ancestor .cf-drawer-item[data-line-key], optimistically
  // removes the line from the rendered cart, then calls /cart/change.js
  // with quantity: 0 to settle. Disables the button during the request to
  // prevent double-tap. Folded into the same optimistic-render + settle
  // pattern as the stepper so it can't race with concurrent stepper clicks.
  function handleRemoveClick(e) {
    var target = e.target.closest && e.target.closest('[data-cf-drawer-remove]');
    if (!target || target.disabled) return;
    var lineKey = findLineKeyFromTarget(target);
    if (!lineKey) return;

    // Disable the remove button immediately to prevent double-tap.
    target.disabled = true;

    // Optimistic render — show line gone right away.
    var predicted = predictCartAfterQtyChange(_lastCart, lineKey, 0);
    if (predicted) renderDrawer(predicted, true);

    removeLineByKey(lineKey).then(function (cart) {
      if (cart) renderDrawer(cart, true);
    }).catch(function () {
      // Failure — re-enable button and re-fetch authoritative state.
      target.disabled = false;
      return getCartState().then(function (cart) {
        if (cart) renderDrawer(cart);
      }).catch(function () {});
    });
  }

  // Stepper click handler — delegated from document. Reads the +1/-1
  // delta from data-cf-drawer-step on the button, walks up to find the
  // line key, optimistically re-renders the cart with the new qty (or
  // line-removed when newQty drops to 0), then issues /cart/change.js
  // to settle. No gift sync, no concurrent-chain race guards — each
  // line is independent.
  function handleStepperClick(e) {
    var target = e.target.closest && e.target.closest('[data-cf-drawer-step]');
    if (!target || target.disabled) return;
    var delta = parseInt(target.getAttribute('data-cf-drawer-step'), 10);
    if (isNaN(delta) || delta === 0) return;
    var lineKey = findLineKeyFromTarget(target);
    if (!lineKey || !_lastCart || !_lastCart.items) return;

    var currentItem = null;
    for (var i = 0; i < _lastCart.items.length; i++) {
      if (_lastCart.items[i].key === lineKey) { currentItem = _lastCart.items[i]; break; }
    }
    if (!currentItem) return;
    var newQty = Math.max(0, (currentItem.quantity || 0) + delta);

    // Disable both buttons on the line briefly to prevent double-fires.
    var row = target.closest('.cf-drawer-item');
    var btns = row ? row.querySelectorAll('[data-cf-drawer-step]') : [];
    for (var b = 0; b < btns.length; b++) btns[b].disabled = true;

    var predicted = predictCartAfterQtyChange(_lastCart, lineKey, newQty);
    if (predicted) renderDrawer(predicted, newQty === 0);

    changeLineQty(lineKey, newQty).then(function (cart) {
      if (cart) renderDrawer(cart, newQty === 0);
    }).catch(function () {
      // Failure — re-fetch authoritative cart.
      return getCartState().then(function (cart) {
        if (cart) renderDrawer(cart);
      }).catch(function () {});
    });
  }

  // ------------------------------------------------------------------
  // /cart page bootstrap
  //
  // On the /cart route, reconcile once on load. If the reconciler
  // actually mutated state (gifts added or removed), reload so the
  // theme-rendered cart line items reflect the new state. We detect
  // mutation by comparing item_count + total_price between the
  // pre-sync cart and the post-sync cart.
  // ------------------------------------------------------------------
  function bootCartPage() {
    // No-op now that bundles are real products with no orphan-sync needed.
    // Kept as a stub so future code paths can hook in here cleanly.
    return;
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    // Delegate stepper +/- clicks (works for any cart line in the drawer).
    document.addEventListener('click', handleStepperClick, false);

    // Delegate per-line × remove clicks (works for any cart line in the drawer).
    document.addEventListener('click', handleRemoveClick, false);

    // Drawer-open hook — paints _lastCart immediately, then refreshes
    // from /cart.js. Capture phase so we paint BEFORE the drawer-agent's
    // bubble-phase slide-in handler.
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      // Skip the stepper and remove buttons — they're handled separately.
      if (e.target.closest('[data-cf-drawer-step]')) return;
      if (e.target.closest('[data-cf-drawer-remove]')) return;
      var trigger = e.target.closest('[data-cf-drawer-open]');
      if (!trigger) return;

      hideCartError();

      var container = document.getElementById('cf-cart-drawer-items');
      if (_lastCart) {
        renderDrawer(_lastCart);
      } else if (container) {
        renderSkeleton(container);
      }

      getCartState().then(function (cart) {
        if (cart) renderDrawer(cart);
      }).catch(function () { /* swallow — drawer still opens */ });
    }, true);

    // Hydrate cart state on page load. This fixes three things at once:
    //  (a) the header cart badge shows the true item_count immediately and
    //      persists across drawer open/close (was a hardcoded 0 nothing updated);
    //  (b) the optimistic add-to-cart base (_lastCart) already contains any lines
    //      already in the cart, so adding a 2nd item paints BOTH lines at once
    //      instead of the existing line "popping in" seconds later; and
    //  (c) the first drawer open is instant with warm, decoded thumbnails.
    getCartState().then(function (cart) {
      if (!cart) return;
      _lastCart = cart;
      _lastCartAt = Date.now();
      updateCartBadges(cart);
      if (cart.items && cart.items.length) renderDrawer(cart, true);
    }).catch(function () { /* swallow — badge stays hidden, drawer still works */ });

    bootCartPage();
  }

  // ------------------------------------------------------------------
  // Public API — simplified after bundle refactor (2026-05-29).
  // PDP no longer needs variant-cache or synthetic-line helpers since
  // each bundle is one product = one cart line.
  // ------------------------------------------------------------------
  window.cfCart = {
    renderDrawer: renderDrawer,
    syncGifts: syncGifts, // no-op shim; kept for backwards compat
    reconcile: syncGifts,
    getCart: getCartState,
    showError: showCartError,
    hideError: hideCartError,
    getLastCart: function () { return _lastCart; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
